import { supabase } from '../lib/supabase';

export interface UserLog {
  id: string;
  user_id: string;
  action: string;
  description?: string;
  metadata?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  created_at: string;
}

export interface CreateLogEntry {
  action: string;
  description?: string;
  metadata?: Record<string, any>;
}

export class UserLoggingService {
  /**
   * Create a new log entry for the current user
   */
  static async logUserActivity(entry: CreateLogEntry): Promise<{ success: boolean; data?: UserLog; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('log_user_activity', {
        p_action: entry.action,
        p_description: entry.description || null,
        p_metadata: entry.metadata || {}
      });

      if (error) {
        console.error('Error logging user activity:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data };
    } catch (error) {
      console.error('Unexpected error logging user activity:', error);
      return { success: false, error: 'Failed to log user activity' };
    }
  }

  /**
   * Get user logs for the current authenticated user
   */
  static async getUserLogs(limit: number = 50, offset: number = 0): Promise<{ success: boolean; data?: UserLog[]; error?: string; total?: number }> {
    try {
      // Get total count
      const { count, error: countError } = await supabase
        .from('user_logging')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        console.error('Error getting log count:', countError);
        return { success: false, error: countError.message };
      }

      // Get paginated logs
      const { data, error } = await supabase
        .from('user_logging')
        .select('*')
        .order('timestamp', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching user logs:', error);
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        data: data || [], 
        total: count || 0 
      };
    } catch (error) {
      console.error('Unexpected error fetching user logs:', error);
      return { success: false, error: 'Failed to fetch user logs' };
    }
  }

  /**
   * Get logs by action type
   */
  static async getLogsByAction(action: string, limit: number = 20): Promise<{ success: boolean; data?: UserLog[]; error?: string; total?: number }> {
    try {
      const { data, error } = await supabase
        .from('user_logging')
        .select('*')
        .eq('action', action)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching logs by action:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [], total: data?.length || 0 };
    } catch (error) {
      console.error('Unexpected error fetching logs by action:', error);
      return { success: false, error: 'Failed to fetch logs by action' };
    }
  }

  /**
   * Get logs within a date range
   */
  static async getLogsByDateRange(
    startDate: Date, 
    endDate: Date, 
    limit: number = 100
  ): Promise<{ success: boolean; data?: UserLog[]; error?: string; total?: number }> {
    try {
      const { data, error } = await supabase
        .from('user_logging')
        .select('*')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString())
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching logs by date range:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [], total: data?.length || 0 };
    } catch (error) {
      console.error('Unexpected error fetching logs by date range:', error);
      return { success: false, error: 'Failed to fetch logs by date range' };
    }
  }

  /**
   * Common logging actions for easy use
   */
  static async logLogin(loginMethod: string = 'email') {
    return this.logUserActivity({
      action: 'LOGIN',
      description: 'User logged in',
      metadata: { method: loginMethod, timestamp: new Date().toISOString() }
    });
  }

  static async logLogout() {
    return this.logUserActivity({
      action: 'LOGOUT',
      description: 'User logged out',
      metadata: { timestamp: new Date().toISOString() }
    });
  }

  static async logSettingsUpdate(changedFields: string[]) {
    return this.logUserActivity({
      action: 'SETTINGS_UPDATED',
      description: 'User updated account settings',
      metadata: { 
        changedFields, 
        timestamp: new Date().toISOString() 
      }
    });
  }

  static async logPasswordChange() {
    return this.logUserActivity({
      action: 'PASSWORD_CHANGED',
      description: 'User changed password',
      metadata: { timestamp: new Date().toISOString() }
    });
  }

  static async logCampaignCreated(campaignId: string, campaignName: string) {
    return this.logUserActivity({
      action: 'CAMPAIGN_CREATED',
      description: `Created campaign: ${campaignName}`,
      metadata: { 
        campaignId, 
        campaignName, 
        timestamp: new Date().toISOString() 
      }
    });
  }

  static async logQRCodeGenerated(qrCodeId: string, destination: string) {
    return this.logUserActivity({
      action: 'QR_CODE_GENERATED',
      description: 'Generated QR code',
      metadata: {
        qrCodeId,
        destination,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Campaign Management
  static async logCampaignUpdated(campaignId: string, campaignName: string, changes: string[]) {
    return this.logUserActivity({
      action: 'CAMPAIGN_UPDATED',
      description: `Updated campaign: ${campaignName}`,
      metadata: {
        campaignId,
        campaignName,
        changes,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logCampaignDeleted(campaignId: string, campaignName: string) {
    return this.logUserActivity({
      action: 'CAMPAIGN_DELETED',
      description: `Deleted campaign: ${campaignName}`,
      metadata: {
        campaignId,
        campaignName,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logCampaignStatusChanged(campaignId: string, campaignName: string, oldStatus: string, newStatus: string) {
    return this.logUserActivity({
      action: 'CAMPAIGN_STATUS_CHANGED',
      description: `Changed campaign status: ${campaignName} from ${oldStatus} to ${newStatus}`,
      metadata: {
        campaignId,
        campaignName,
        oldStatus,
        newStatus,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logCampaignDuplicated(originalId: string, newId: string, campaignName: string) {
    return this.logUserActivity({
      action: 'CAMPAIGN_DUPLICATED',
      description: `Duplicated campaign: ${campaignName}`,
      metadata: {
        originalCampaignId: originalId,
        newCampaignId: newId,
        campaignName,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Payment & Billing
  static async logPaymentInitiated(billId: string, amount: number, campaignId?: string) {
    return this.logUserActivity({
      action: 'PAYMENT_INITIATED',
      description: `Payment initiated: RM ${amount.toFixed(2)}`,
      metadata: {
        billId,
        amount,
        campaignId,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logPaymentCompleted(billId: string, amount: number, paymentMethod?: string, campaignId?: string) {
    return this.logUserActivity({
      action: 'PAYMENT_COMPLETED',
      description: `Payment completed: RM ${amount.toFixed(2)}`,
      metadata: {
        billId,
        amount,
        paymentMethod,
        campaignId,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logPaymentFailed(billId: string, amount: number, reason?: string, campaignId?: string) {
    return this.logUserActivity({
      action: 'PAYMENT_FAILED',
      description: `Payment failed: RM ${amount.toFixed(2)}`,
      metadata: {
        billId,
        amount,
        reason,
        campaignId,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logInvoiceGenerated(invoiceNumber: string, billId: string, amount: number) {
    return this.logUserActivity({
      action: 'INVOICE_GENERATED',
      description: `E-invoice generated: ${invoiceNumber}`,
      metadata: {
        invoiceNumber,
        billId,
        amount,
        timestamp: new Date().toISOString()
      }
    });
  }

  // QR Code Management
  static async logQRCodeUpdated(qrCodeId: string, changes: string[]) {
    return this.logUserActivity({
      action: 'QR_CODE_UPDATED',
      description: 'Updated QR code settings',
      metadata: {
        qrCodeId,
        changes,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logQRCodeDeleted(qrCodeId: string) {
    return this.logUserActivity({
      action: 'QR_CODE_DELETED',
      description: 'Deleted QR code',
      metadata: {
        qrCodeId,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Media Management
  static async logMediaUploaded(mediaType: string, fileName: string, fileSize: number, purpose?: string) {
    return this.logUserActivity({
      action: 'MEDIA_UPLOADED',
      description: `Uploaded ${mediaType}: ${fileName}`,
      metadata: {
        mediaType,
        fileName,
        fileSize,
        purpose,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logMediaDeleted(mediaType: string, fileName: string) {
    return this.logUserActivity({
      action: 'MEDIA_DELETED',
      description: `Deleted ${mediaType}: ${fileName}`,
      metadata: {
        mediaType,
        fileName,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Profile & Account
  static async logProfileUpdated(changes: string[]) {
    return this.logUserActivity({
      action: 'PROFILE_UPDATED',
      description: 'Updated profile information',
      metadata: {
        changes,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logEmailChanged(oldEmail: string, newEmail: string) {
    return this.logUserActivity({
      action: 'EMAIL_CHANGED',
      description: 'Changed email address',
      metadata: {
        oldEmail,
        newEmail,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logNotificationPreferencesUpdated(preferences: Record<string, boolean>) {
    return this.logUserActivity({
      action: 'NOTIFICATION_PREFERENCES_UPDATED',
      description: 'Updated notification preferences',
      metadata: {
        preferences,
        timestamp: new Date().toISOString()
      }
    });
  }

  // Security Events
  static async logFailedLoginAttempt(email: string, reason?: string) {
    return this.logUserActivity({
      action: 'FAILED_LOGIN_ATTEMPT',
      description: 'Failed login attempt',
      metadata: {
        email,
        reason,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logPasswordResetRequested(email: string) {
    return this.logUserActivity({
      action: 'PASSWORD_RESET_REQUESTED',
      description: 'Requested password reset',
      metadata: {
        email,
        timestamp: new Date().toISOString()
      }
    });
  }

  static async logSessionExpired() {
    return this.logUserActivity({
      action: 'SESSION_EXPIRED',
      description: 'Session expired',
      metadata: {
        timestamp: new Date().toISOString()
      }
    });
  }
}

export default UserLoggingService;
